import type { AdGroupSummary, MetricSnapshot, SkuBreakdown } from "@adpilot/shared";
/** Anything with a report-backed metric snapshot (ad groups, targets). */
export interface MetricSource {
  hasMetrics: boolean;
  metrics: MetricSnapshot;
  skuBreakdown: SkuBreakdown | null;
  defaultBid?: number | null;
}
export const AD_GROUP_COLUMNS = [
  "status",
  "defaultBid",
  "cpc",
  "cpa",
  "ctr",
  "cvr",
  "acos",
  "roas",
  "spend",
  "topOfSearch",
  "impressions",
  "clicks",
  "orders",
  "units",
  "sales",
  "advertisedOrders",
  "advertisedUnits",
  "advertisedSales",
  "otherOrders",
  "otherUnits",
  "otherSales",
] as const;
export type GroupColumn = (typeof AD_GROUP_COLUMNS)[number];
export const SKU_BREAKDOWN_COLUMNS = [
  "advertisedOrders",
  "advertisedUnits",
  "advertisedSales",
  "otherOrders",
  "otherUnits",
  "otherSales",
] as const satisfies readonly GroupColumn[];
type SkuColumn = (typeof SKU_BREAKDOWN_COLUMNS)[number];
export const isSkuColumn = (key: GroupColumn): key is SkuColumn =>
  (SKU_BREAKDOWN_COLUMNS as readonly string[]).includes(key);
export type GroupSortKey = GroupColumn | "name";
export const MONETARY_COLUMNS: readonly GroupColumn[] = [
  "defaultBid",
  "spend",
  "sales",
  "cpc",
  "cpa",
  "advertisedSales",
  "otherSales",
];
const divide = (a: number, b: number) => (b > 0 ? a / b : null);
export function groupMetric(row: MetricSource, key: GroupColumn): number | null {
  if (key === "defaultBid") return row.defaultBid ?? null;
  if (key === "status" || key === "topOfSearch" || !row.hasMetrics) return null;
  if (isSkuColumn(key)) return row.skuBreakdown?.[key] ?? null;
  const m = row.metrics;
  switch (key) {
    case "cpc":
      return divide(m.spend, m.clicks);
    case "cpa":
      return divide(m.spend, m.orders);
    case "ctr":
      return divide(m.clicks, m.impressions);
    case "cvr":
      return divide(m.orders, m.clicks);
    case "acos":
      return divide(m.spend, m.sales);
    case "roas":
      return divide(m.sales, m.spend);
    default:
      return m[key];
  }
}
export function aggregateGroups(rows: readonly MetricSource[]): AdGroupSummary {
  const metrics = {
    impressions: 0,
    clicks: 0,
    spend: 0,
    sales: 0,
    orders: 0,
    units: 0,
    ctr: 0,
    cvr: 0,
    acos: 0,
    roas: 0,
    cpc: 0,
    cpa: 0,
  };
  for (const row of rows)
    for (const key of ["impressions", "clicks", "spend", "sales", "orders", "units"] as const)
      metrics[key] += row.metrics[key];
  // Breakdown totals are only honest when every row carries one.
  const skuBreakdown =
    rows.length > 0 && rows.every((r) => r.skuBreakdown)
      ? Object.fromEntries(
          SKU_BREAKDOWN_COLUMNS.map((key) => [
            key,
            rows.reduce((sum, r) => sum + (r.skuBreakdown?.[key] ?? 0), 0),
          ]),
        )
      : null;
  return {
    skuBreakdown: skuBreakdown as AdGroupSummary["skuBreakdown"],
    id: "total",
    name: "",
    status: "enabled",
    defaultBid: null,
    // Rows without a report contribute nothing, so one is enough for an honest sum.
    hasMetrics: rows.some((r) => r.hasMetrics),
    aiManaged: false,
    targetCount: 0,
    metrics,
  };
}
export function sortGroups<T extends MetricSource & { name: string; status: string }>(
  rows: readonly T[],
  key: GroupSortKey,
  direction: "asc" | "desc",
): T[] {
  return [...rows].sort((a, b) => {
    const x = key === "name" ? a.name : key === "status" ? a.status : groupMetric(a, key);
    const y = key === "name" ? b.name : key === "status" ? b.status : groupMetric(b, key);
    if (x === null) return y === null ? 0 : 1;
    if (y === null) return -1;
    return (
      (typeof x === "string" && typeof y === "string"
        ? x.localeCompare(y)
        : Number(x) - Number(y)) * (direction === "asc" ? 1 : -1)
    );
  });
}
export function formatGroupMetric(
  value: number | null,
  key: GroupColumn,
  currency: string | null,
  locale: string,
): string {
  if (value === null || !Number.isFinite(value)) return "—";
  if (MONETARY_COLUMNS.includes(key)) {
    if (!currency) return "—";
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currencyDisplay: "narrowSymbol",
        currency,
      }).format(value);
    } catch {
      return "—";
    }
  }
  return new Intl.NumberFormat(
    locale,
    ["ctr", "cvr", "acos"].includes(key)
      ? { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { maximumFractionDigits: key === "roas" ? 2 : 0 },
  ).format(value);
}
/** Excel-safe CSV: BOM, quoted cells, formula-leading characters neutralised. */
export function csvDocument(rows: readonly (readonly string[])[]): string {
  // Bare dashes ("--" placeholders) cannot form a formula, so they stay as shown.
  const escapeCell = (text: string) =>
    `"${(/^[=+@\-\t\r]/.test(text) && !/^-+$/.test(text) ? `'${text}` : text).replaceAll('"', '""')}"`;
  return `\uFEFF${rows.map((row) => row.map(escapeCell).join(",")).join("\r\n")}`;
}
export function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  // Firefox/Safari may start the download after click() returns; revoke on the next tick.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
export function exportGroupsCsv(
  rows: readonly (MetricSource & { name: string })[],
  columns: GroupSortKey[],
  label: (key: GroupSortKey) => string,
  currency: string | null,
  locale: string,
): string {
  return csvDocument([
    columns.map(label),
    ...rows.map((row) =>
      columns.map((key) =>
        key === "name"
          ? row.name
          : key === "status"
            ? "—"
            : formatGroupMetric(groupMetric(row, key), key, currency, locale),
      ),
    ),
  ]);
}
